import React, {useEffect, useRef, useState} from "react";
import {Button, Spinner} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../../../../hooks/useTypedSelector";
import {CurrentUserSliceDataModel} from "../../../login/infrastructure/slices/currentUserSlice/currentUserSliceModel";
import {ModalBody, ModalFooter, ModalHeader, ModalWrapper} from "../../../../shared/Components/modal/Modal";
import {CornyBakersEnv} from "../../../../shared/environment/features/CornyBakersEnvironment";
import {
    fetchUnreadWebshopNotifications,
    markWebshopNotificationSeen,
    resetWebshopNotifications
} from "../../infrastructure/slices/webshopNotificationSlice/webshopNotificationSlice";

const API_BASE_URL = CornyBakersEnv.API_BASE_URL;

type Props = {
    user?: CurrentUserSliceDataModel;
};

const rewriteNotificationContent = (content: string) => {
    const doc = document.createElement('div');
    doc.innerHTML = content;

    const images = doc.getElementsByTagName('img');
    for (const image of images) {
        const src = image.getAttribute('src');
        if (src && src.startsWith('/')) {
            image.src = `${API_BASE_URL}${src}`;
        }
    }

    return doc.innerHTML;
};

export function WebshopNotificationModal({user}: Props) {
    const {t} = useTranslation('common');
    const dispatch = useAppDispatch();
    const previousUserId = useRef<number | undefined>(undefined);
    const [dismissedNotificationIds, setDismissedNotificationIds] = useState<number[]>([]);

    const {notifications, loading, loaded, seenLoading} = useAppSelector(state => state.webshopNotification);

    useEffect(() => {
        if (!user?.id) {
            previousUserId.current = undefined;
            setDismissedNotificationIds([]);
            dispatch(resetWebshopNotifications());
            return;
        }

        if (previousUserId.current !== user.id) {
            previousUserId.current = user.id;
            setDismissedNotificationIds([]);
            dispatch(resetWebshopNotifications());
            dispatch(fetchUnreadWebshopNotifications());
            return;
        }

        if (!loaded && !loading) {
            dispatch(fetchUnreadWebshopNotifications());
        }
    }, [dispatch, loaded, loading, user?.id]);

    const currentNotification = notifications.find(notification => !dismissedNotificationIds.includes(notification.id));
    const isVisible = Boolean(currentNotification);

    const handleSeen = () => {
        if (!currentNotification || seenLoading) {
            return;
        }

        dispatch(markWebshopNotificationSeen(currentNotification.id));
    };

    return (
        <ModalWrapper
            show={isVisible}
            backdrop="static"
            keyboard={false}
            colorVariant="primary"
        >
            {currentNotification && <>
                <ModalHeader title={currentNotification.title} />
                <ModalBody>
                    <div className="d-flex flex-column gap-3">
                        <div
                            className="webshop-notification-content"
                            dangerouslySetInnerHTML={{__html: rewriteNotificationContent(currentNotification.content)}}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="primary" onClick={handleSeen} disabled={seenLoading}>
                        {seenLoading && (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                &nbsp;
                            </>
                        )}
                        {t('Mark as seen')}
                    </Button>
                </ModalFooter>
            </>}
        </ModalWrapper>
    );
}
